/**
 * @file table.component.tsx
 * @description Form-control обертка для работы с Table feature компонентом в react-hook-form
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-11
 * @dependencies [react, react-hook-form, @features/table, @features/object-form]
 * @related-files [@features/table/table.component.tsx, @features/object-form/object-form.tsx]
 */

import { Table as DefaultTable } from '@features/table';
import { Control, Controller } from 'react-hook-form';
import { IBaseObjectDTO, TBaseObjectDto } from '@src/types';
import { useRef, useState } from 'react';
import { ObjectForm } from '@features/object-form';
import { getObjectEntity } from '@utils/form-object-helper';
import cn from 'classnames';
import { AddRowButton } from '@components/ui/custom/buttons/add-row-button';
import { generateFakeGuid } from '@utils/data-helper';
import { EmptyBody } from '@features/table/components';
import { IColumnConfig } from '../../../types/pages';

/**
 * Props для form-control компонента Table с интеграцией react-hook-form
 */
interface ITable extends Omit<React.ComponentProps<typeof DefaultTable>, 'items'> {
    /** Control объект из react-hook-form для управления формой */
    control: Control;
    /** Имя поля в форме */
    name: string;
    /** Тип объектов для отображения в таблице */
    type: string;
    /** Конфигурация колонок таблицы */
    columns: IColumnConfig[];
    /** Тип объекта для создания новых элементов */
    addItemType?: string;
    /** Разрешить клики по строкам таблицы */
    isRowClickable?: boolean;
    /** Разрешить добавление новых строк */
    isCanAddRows?: boolean;
    className?: string;
    initialItem?: IBaseObjectDTO;
    excludeIds?: TBaseObjectDto[];
    onChangeHandler?: (name: string, value: TBaseObjectDto) => void;
    isShowEmptyBody?: boolean;
}

export const Table = ({
    control,
    name,
    type,
    addItemType = 'new',
    isRowClickable = false,
    isCanAddRows = false,
    className,
    initialItem,
    excludeIds = [],
    onChangeHandler,
    isShowEmptyBody,
    ...props
}: ITable) => {
    const [openedObject, setOpenedObject] = useState<TBaseObjectDto | null>(null);
    const objectEntityRef = useRef<TBaseObjectDto | null>(null);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                return (
                    <>
                        {isShowEmptyBody && (!field.value || !field.value?.length) ? (
                            <EmptyBody />
                        ) : (
                            <DefaultTable
                                {...props}
                                items={field.value}
                                className={cn(className, { 'clickable-rows': isRowClickable })}
                                onRowClick={(row: TBaseObjectDto) => {
                                    setOpenedObject(row.$item);
                                    objectEntityRef.current = getObjectEntity(row.$item.$type);
                                }}
                                onChange={(value: TBaseObjectDto) => {
                                    field.onChange(value);
                                    onChangeHandler && onChangeHandler(name, value);
                                }}
                            />
                        )}

                        {isRowClickable && openedObject?.$type && objectEntityRef.current && (
                            <ObjectForm
                                type={openedObject?.$type}
                                initialItem={openedObject}
                                onClose={() => {
                                    setOpenedObject(null);
                                    objectEntityRef.current = null;
                                }}
                                {...objectEntityRef.current}
                            />
                        )}

                        {isCanAddRows && (
                            <AddRowButton
                                right="37.4858vmin"
                                bottom="4.2342vmin"
                                type={type}
                                isAddFromCatalog={addItemType === 'catalog'}
                                initialItem={initialItem}
                                currentItems={[...(field.value || []), ...excludeIds]}
                                onDataUpdate={(newItem: TBaseObjectDto) => {
                                    const newItemsList = [
                                        ...(field.value || []),
                                        { ...newItem, id: generateFakeGuid() }
                                    ];
                                    field.onChange(newItemsList);
                                    onChangeHandler && onChangeHandler(name, newItemsList);
                                }}
                            />
                        )}
                    </>
                );
            }}
        />
    );
};
